import i18n from '@/lib/i18n/client';

export interface UserDetails {
  userId?: string;
  userToken?: string;
  userAgent?: string;
  userLocale?: string;
}

export interface ErrorEvent {
  errorType: string;
  errorMessage: string;
  errorStack: string;
  errorInfo: string;
  userDetails?: UserDetails;
}

const hasProperty = (obj: ExplicitAny, prop: string): boolean => prop in obj;

export const logErrorToDataLayer = (
  error: unknown,
  errorType: string,
  userDetails?: UserDetails
) => {
  const dataLayer = (window as ExplicitAny)?.dataLayer;
  if (!dataLayer) {
    return;
  }

  const errorEvent: ErrorEvent = {
    errorType: errorType,
    errorMessage: 'Unknown error',
    errorStack: 'No stack trace',
    errorInfo: 'No URL',
    userDetails: {
      userAgent: navigator.userAgent,
      userLocale: i18n.language,
      ...userDetails,
    },
  };

  if (error && typeof error === 'object') {
    if (hasProperty(error, 'message')) {
      errorEvent.errorMessage =
        (error as ExplicitAny)?.message ?? 'Unknown error';
      errorEvent.errorStack = (error as ExplicitAny)?.stack ?? 'No stack trace';

      if (
        hasProperty(error, 'config') &&
        hasProperty((error as ExplicitAny)?.config, 'url')
      ) {
        errorEvent.errorInfo = (error as ExplicitAny).config?.url;
      }
    }

    if (
      hasProperty(error, 'preventDefault') &&
      hasProperty(error, 'nativeEvent')
    ) {
      const nativeError = (error as ExplicitAny)?.nativeEvent;
      if (nativeError && nativeError?.type) {
        errorEvent.errorMessage = nativeError.type ?? 'Synthetic event error';
        errorEvent.errorInfo = 'No URL';
      }
    }

    if (hasProperty(error, 'status') && hasProperty(error, 'message')) {
      errorEvent.errorMessage =
        (error as ExplicitAny).message ?? 'Unknown network error';
      errorEvent.errorInfo = `Status: ${(error as ExplicitAny).status ?? 'No status'}`;
    }
  }

  dataLayer.push({
    event: 'Error',
    ...errorEvent,
  });
};
